import React from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { submitFeedback } from 'lib/Api/Feedback';
import { FeedbackCategory } from 'lib/Model/Feedback';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginTop: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
  },
  introText: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  successBox: {
    textAlign: 'center',
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
  },
  buttonWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const FeedbackView: React.FC = () => {
  const classes = useStyles();

  const categoryOptions: FeedbackCategory[] = [
    { value: 'idea', label: 'Idea / Suggestion' },
    { value: 'concern', label: 'Workplace Concern' },
    { value: 'kudos', label: 'Kudos / Positive Feedback' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'other', label: 'Other' },
  ];

  const [category, setCategory] = React.useState<string>('');
  const [feedback, setFeedback] = React.useState('');
  const [name, setName] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await submitFeedback({
        category,
        message: feedback,
        submitterName: name,
      });

      setIsSubmitting(false);
      setIsSubmitted(true);
    } catch (err) {
      setIsSubmitting(false);
      setError(
        'There was an error submitting your feedback. Please try again.',
      );
    }
  };

  const handleReset = () => {
    setCategory('');
    setFeedback('');
    setName('');
    setIsSubmitted(false);
    setError(null);
  };

  const handleCloseError = () => {
    setError(null);
  };

  if (isSubmitted) {
    return (
      <Container maxWidth="sm" className={classes.container}>
        <Box className={classes.successBox}>
          <Typography variant="h5" component="h2" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" className={classes.introText}>
            Your feedback has been received. We take every submission seriously
            and appreciate you taking the time to help us improve.
          </Typography>
          <Button
            onClick={handleReset}
            variant="contained"
            color="primary"
            style={{ marginTop: '16px' }}
          >
            Submit More Feedback
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className={classes.container}>
      <Typography variant="h4" component="h1" className={classes.title}>
        Your Voice Matters
      </Typography>
      <Typography variant="body1" className={classes.introText}>
        Got a suggestion? A question? Something bugging you? <br></br>
        <br></br>Your feedback is essential for making KonnektTalexio a better
        place to work. All submissions are only received by our HR team and are
        fully confidential.
      </Typography>
      <form onSubmit={handleSubmit} className={classes.form}>
        <TextField
          label="Your Name"
          variant="outlined"
          value={name}
          autoFocus
          InputLabelProps={{ shrink: true }}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <Autocomplete
          id="feedback-category"
          options={categoryOptions}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.label
          }
          value={
            categoryOptions.find((option) => option.value === category) || null
          }
          onChange={(_, newValue) => {
            setCategory(newValue ? newValue.value : '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Category"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        <TextField
          label="Your Feedback"
          variant="outlined"
          multiline
          rows={6}
          InputLabelProps={{ shrink: true }}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          required
          helperText="Please be as specific and constructive as possible."
        />

        <Box display="flex" justifyContent="flex-end">
          <div className={classes.buttonWrapper}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              Submit Feedback
            </Button>
            {isSubmitting && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        </Box>
      </form>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={handleCloseError}
        >
          {error}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default FeedbackView;
